import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, X } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setEmail('');
      setShowNewsletter(false);
    }, 2000);
  };

  return (
    <footer className="bg-neutral-100 border-t border-neutral-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">{t.footer.newsletter}</h3>
            <button 
              onClick={() => setShowNewsletter(true)}
              className="px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors"
            >
              {t.footer.subscribe}
            </button>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">{t.footer.contact}</h3>
            <a href="/contact" className="inline-block px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
              {t.footer.contactBtn}
            </a>
          </div>

          {/* Logo */}
          <div className="sm:col-span-2 lg:col-span-1 flex flex-col items-start lg:items-end">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-600 rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <span className="text-2xl font-semibold text-neutral-800">SRE GLOBAL</span>
            </div>
          </div>
        </div>

        {/* Licenses Information */}
        <div className="bg-teal-50 border-l-4 border-teal-600 p-4 md:p-6 mb-8 rounded">
          <h4 className="font-semibold text-neutral-900 mb-3">{t.footer.regulatedTitle}</h4>
          <div className="space-y-2 text-sm text-neutral-700">
            <p>
              <strong>{t.footer.spkLicense}</strong> {t.footer.spkLicenseText}
            </p>
            <p>
              <strong>{t.footer.fcaLicense}</strong> {t.footer.fcaLicenseText}
            </p>
            <p className="text-xs mt-3 text-neutral-600">
              {t.footer.licenseNote}
            </p>
          </div>
        </div>

        {/* Important Notice / Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 md:p-5 mb-8 rounded" data-testid="footer-disclaimer">
          <div className="flex items-start gap-3">
            <span className="bg-amber-500 text-white px-2 py-0.5 rounded text-xs font-semibold shrink-0 mt-0.5">
              {t.home.disclaimer}
            </span>
            <p className="text-sm text-neutral-700 leading-relaxed">
              {t.home.disclaimerText}
            </p>
          </div>
        </div>

        {/* Bottom Links */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-neutral-300 gap-4">
          <p className="text-sm text-neutral-600 text-center md:text-left">{t.footer.copyright}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
            <a href="/privacy-policy" className="text-neutral-600 hover:text-teal-600 transition-colors whitespace-nowrap">
              {t.footer.privacyPolicy}
            </a>
            <a href="/terms-conditions" className="text-neutral-600 hover:text-teal-600 transition-colors whitespace-nowrap">
              {t.footer.terms}
            </a>
            <a href="/cookie-policy" className="text-neutral-600 hover:text-teal-600 transition-colors whitespace-nowrap">
              {t.footer.cookiePolicy}
            </a>
          </div>
        </div>
      </div>

      {/* Newsletter Popup Modal */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 md:p-8 relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowNewsletter(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X size={24} />
            </button>

            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-neutral-900 mb-2">{t.footer.newsletterSuccessTitle}</h3>
                <p className="text-neutral-700">{t.footer.newsletterSuccessDesc}</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-neutral-900 mb-2">{t.footer.newsletterPopupTitle}</h3>
                <p className="text-neutral-600 mb-6">
                  {t.footer.newsletterPopupDesc}
                </p>
                <form onSubmit={handleNewsletterSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t.footer.newsletterEmailLabel} *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                      placeholder={t.footer.newsletterEmailPlaceholder}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors font-semibold"
                  >
                    {t.footer.newsletterSubscribeBtn}
                  </button>
                  <p className="text-xs text-neutral-500 mt-4 text-center">
                    {t.footer.newsletterDisclaimer}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
